import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, X, Send, Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = 'sov_wingman_session';

const HIDDEN_ROUTES = []; // Sovereign Assistant must be visible on ALL pages (fleet-wide standard)

const SUGGESTED = [
  'Explain the Nigeria-to-Hawaii bridge.',
  'How do I begin boarding?',
  'What KYC documents will I need?',
  'Tell me about the Banana Island estate.',
];

const getContext = (pathname) => {
  if (pathname.startsWith('/vault')) return 'vault';
  if (pathname.startsWith('/boarding')) return 'boarding';
  if (pathname.startsWith('/valuation')) return 'valuation';
  if (pathname.startsWith('/global-bridge')) return 'global bridge';
  return 'home';
};

const Wingman = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `wm_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  });
  const scrollRef = useRef(null);

  const hidden = HIDDEN_ROUTES.some((r) => location.pathname.startsWith(r));

  // Load history on mount / when opening
  useEffect(() => {
    if (!open || messages.length > 0) return;
    axios.get(`${API}/wingman/history/${sessionId}`)
      .then((r) => setMessages(r.data.messages || []))
      .catch(() => {});
  }, [open, sessionId, messages.length]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Lock body scroll when modal open on mobile
  useEffect(() => {
    if (open && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput('');
    const optimistic = { role: 'user', content: msg, created_at: new Date().toISOString() };
    setMessages((m) => [...m, optimistic]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/wingman/chat`, {
        session_id: sessionId,
        message: msg,
        context: getContext(location.pathname),
      });
      setMessages((m) => [...m, { role: 'assistant', content: data.reply, created_at: new Date().toISOString() }]);
    } catch (err) {
      setMessages((m) => [...m, {
        role: 'assistant',
        content: 'The Wingman is consulting Mercy\'s office. Try again in a moment.',
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setSending(false);
    }
  };

  if (hidden) return null;

  return (
    <>
      {/* Sovereign Assistant — circular gold bubble (bottom-right) */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="sovereign-assistant-bubble"
          aria-label="Open Sovereign Assistant"
          className="fixed bottom-6 right-6 z-40 w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#D4AF37] text-[#050505] flex items-center justify-center shadow-[0_0_24px_rgba(212,175,55,0.35)] hover:shadow-[0_0_40px_rgba(212,175,55,0.6)] hover:scale-110 active:scale-95 transition-all group"
        >
          <span className="absolute inset-0 rounded-full bg-[#D4AF37] animate-ping opacity-20" />
          <MessageSquare size={22} strokeWidth={1.75} className="relative" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-[#0A0A0A] border border-[#D4AF37] rounded-full" />
        </button>
      )}

      {/* Wingman pill trigger (secondary, persists for power users) */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="wingman-trigger"
          className="fixed bottom-24 right-6 z-40 group hidden md:block"
          aria-label="Open Wingman"
        >
          <div className="absolute inset-0 bg-[#D4AF37] blur-xl opacity-20 group-hover:opacity-40 transition-opacity"></div>
          <div className="relative flex items-center gap-3 px-5 py-4 bg-[#050505] border border-[#D4AF37] group-hover:bg-[#D4AF37] transition-colors">
            <Sparkles size={16} className="text-[#D4AF37] group-hover:text-[#050505]" strokeWidth={1.5} />
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] group-hover:text-[#050505] hidden sm:inline">
              Wingman
            </span>
            <span className="w-1.5 h-1.5 bg-[#D4AF37] group-hover:bg-[#050505] rounded-full animate-pulse"></span>
          </div>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          data-testid="wingman-panel"
          className="fixed inset-x-0 bottom-0 md:bottom-24 md:right-6 md:left-auto z-50 md:w-[420px] md:max-w-[90vw] bg-[#050505] border border-[#D4AF37]/40 flex flex-col"
          style={{ height: window.innerWidth < 768 ? '92vh' : '620px', maxHeight: '92vh' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-[#D4AF37]/20">
            <div>
              <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase text-[#D4AF37]">
                <Sparkles size={12} strokeWidth={1.5} /> Sovereign Assistant
                <span className="w-1 h-1 bg-[#D4AF37] rounded-full animate-pulse ml-1"></span>
              </div>
              <div className="text-base font-light tracking-wide mt-1">Wingman · Concierge</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="wingman-close"
              className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors"
              aria-label="Close"
            >
              <X size={20} strokeWidth={1.5} />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 space-y-5" data-testid="wingman-messages">
            {messages.length === 0 && !sending && (
              <div className="fade-up">
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-3">Welcome aboard.</div>
                <p className="text-sm font-light leading-relaxed text-[#F5F5F5] mb-6">
                  I am Wingman — the pre-boarding concierge for Sovereign Heritage Command.
                  I can guide you through the Vault, explain the Nigeria-to-Hawaii bridge,
                  or walk you through the boarding application before you meet Mercy.
                </p>
                <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3] mb-3">Begin</div>
                <div className="space-y-2">
                  {SUGGESTED.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => send(s)}
                      data-testid={`wingman-suggested-${i}`}
                      className="block w-full text-left px-4 py-3 border border-[#D4AF37]/20 text-xs tracking-wide text-[#F5F5F5] hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all font-light leading-relaxed"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={i} className={`fade-up ${m.role === 'user' ? 'text-right' : 'text-left'}`}>
                {m.role === 'assistant' && (
                  <div className="text-[9px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">Wingman</div>
                )}
                {m.role === 'user' && (
                  <div className="text-[9px] tracking-[0.4em] uppercase text-[#A3A3A3] mb-2">You</div>
                )}
                <div
                  className={`inline-block max-w-[88%] px-4 py-3 text-sm font-light leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#F5F5F5] text-left'
                      : 'bg-[#0A0A0A] border border-[#D4AF37]/15 text-[#F5F5F5]'
                  }`}
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {sending && (
              <div className="text-left">
                <div className="text-[9px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">Wingman</div>
                <div className="inline-block px-4 py-3 bg-[#0A0A0A] border border-[#D4AF37]/15">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" style={{ animation: 'sov-pulse 1.4s ease-in-out infinite' }}></span>
                    <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" style={{ animation: 'sov-pulse 1.4s ease-in-out 0.2s infinite' }}></span>
                    <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full" style={{ animation: 'sov-pulse 1.4s ease-in-out 0.4s infinite' }}></span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-[#D4AF37]/20 px-6 py-4 flex items-center gap-3"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="wingman-input"
              placeholder="Brief the Wingman…"
              className="flex-1 bg-transparent border-b border-[#2a2a2a] focus:border-[#D4AF37] text-[#F5F5F5] py-2 px-1 outline-none transition-colors text-sm font-light"
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="wingman-send"
              className="w-10 h-10 flex items-center justify-center border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Send"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={1.5} />}
            </button>
          </form>
        </div>
      )}

      <style>{`
        @keyframes sov-pulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </>
  );
};

export default Wingman;
